import type { AccountId } from "@redotech/redo-account-id";
import * as z from "zod";
import {
  TransferBlockedCode,
  type TransferBlockedPayload,
} from "./transfer-blocked";

export const MerchantConsolidationBinCriteriaSchema = z
  .object({
    skus: z.array(z.string()).optional(),
    grades: z.array(z.string()).optional(),
    fulfillable: z.boolean().optional(),
  })
  .strict();

export type MerchantConsolidationBinCriteria = z.infer<
  typeof MerchantConsolidationBinCriteriaSchema
>;

/** A null attribute never matches a criteria parameter on it. */
export interface ConsolidationBinUnit {
  inventoryItemId: string;
  merchantAccountId: AccountId | null;
  sku: string | null;
  grade: string | null;
  disposition: string | null;
  fulfillable: boolean | null;
}

export interface ConsolidationBinCriteriaInput {
  binName: string;
  merchantAccountId: AccountId;
  merchantName: string | null;
  criteria: MerchantConsolidationBinCriteria;
  /** null for a SKU-level placement that carries no license plate. */
  unit: ConsolidationBinUnit | null;
  override: boolean;
}

/**
 * The whole transfer decision for a bin with an active merchant consolidation
 * config: label required, then merchant, then criteria.
 */
export function evaluateConsolidationBinCriteria(
  input: ConsolidationBinCriteriaInput,
): TransferBlockedPayload | null {
  const { binName, merchantName, criteria, unit, override } = input;

  if (unit === null) {
    return {
      code: TransferBlockedCode.consolidationBinItemRequired,
      grade: null,
      disposition: null,
      binName,
      inventoryItemId: null,
      merchantName,
    };
  }

  const base = {
    grade: unit.grade,
    disposition: unit.disposition,
    binName,
    inventoryItemId: unit.inventoryItemId,
  };

  if (override) {
    return null;
  }

  if (unit.merchantAccountId !== input.merchantAccountId) {
    return {
      code: TransferBlockedCode.consolidationBinMerchantMismatch,
      ...base,
      merchantName,
    };
  }

  if (!matchesCriteria(criteria, unit)) {
    return {
      code: TransferBlockedCode.consolidationBinCriteriaMismatch,
      ...base,
      merchantName,
      criteria,
    };
  }

  return null;
}

/** Parameters ANDed, values within a parameter ORed, empty criteria a catch-all. */
function matchesCriteria(
  criteria: MerchantConsolidationBinCriteria,
  unit: ConsolidationBinUnit,
): boolean {
  if (
    criteria.skus !== undefined &&
    (unit.sku === null || !criteria.skus.includes(unit.sku))
  ) {
    return false;
  }
  if (
    criteria.grades !== undefined &&
    (unit.grade === null || !criteria.grades.includes(unit.grade))
  ) {
    return false;
  }
  if (
    criteria.fulfillable !== undefined &&
    (unit.fulfillable === null || unit.fulfillable !== criteria.fulfillable)
  ) {
    return false;
  }
  return true;
}

/** A reservation batch is shelving, not a whole warehouse — keep it reviewable. */
export const MAX_CONSOLIDATION_BIN_BATCH = 200;

export interface ConsolidationBinNamesInput {
  prefix: string;
  count: number;
}

export type ConsolidationBinNames =
  | { names: string[]; error?: undefined }
  | { names?: undefined; error: string };

export function buildConsolidationBinNames({
  count,
  prefix,
}: ConsolidationBinNamesInput): ConsolidationBinNames {
  const trimmed = prefix.trim();
  if (trimmed === "") {
    return { error: "Enter a bin name" };
  }
  if (!Number.isInteger(count) || count < 1) {
    return { error: "Enter how many bins to reserve" };
  }
  if (count > MAX_CONSOLIDATION_BIN_BATCH) {
    return {
      error: `Reserve at most ${MAX_CONSOLIDATION_BIN_BATCH} bins at a time`,
    };
  }

  if (count === 1) {
    return { names: [trimmed] };
  }

  const separator = /[^a-z0-9]$/i.test(trimmed) ? "" : "-";
  const width = Math.max(2, String(count).length);
  const names = Array.from({ length: count }, (_unused, index) => {
    const suffix = String(index + 1).padStart(width, "0");
    return `${trimmed}${separator}${suffix}`;
  });
  return { names };
}

export const ConsolidationBinRunSeparator = {
  dash: "-",
  underscore: "_",
} as const;
export type ConsolidationBinRunSeparator =
  (typeof ConsolidationBinRunSeparator)[keyof typeof ConsolidationBinRunSeparator];

export interface ConsolidationBinRunInput {
  rackName: string;
  rowName: string;
  separator: ConsolidationBinRunSeparator;
  quantity: number;
}

export type ConsolidationBinRun =
  | { names: string[]; request: ConsolidationBinNamesInput; error?: undefined }
  | { names?: undefined; request?: undefined; error: string };

export function buildConsolidationBinRun({
  quantity,
  rackName,
  rowName,
  separator,
}: ConsolidationBinRunInput): ConsolidationBinRun {
  const base = [rowName.trim(), rackName.trim()]
    .filter((part) => part !== "")
    .join(separator);
  if (base === "") {
    return { error: "Enter a row or rack name" };
  }
  const request: ConsolidationBinNamesInput =
    quantity === 1
      ? { count: 1, prefix: `${base}${separator}01` }
      : { count: quantity, prefix: `${base}${separator}` };
  const expansion = buildConsolidationBinNames(request);
  if (expansion.error !== undefined) {
    return { error: expansion.error };
  }
  return { names: expansion.names, request };
}

export const MerchantConsolidationBinFulfillableChoice = {
  any: "any",
  fulfillable: "fulfillable",
  unfulfillable: "unfulfillable",
} as const;
export type MerchantConsolidationBinFulfillableChoice =
  (typeof MerchantConsolidationBinFulfillableChoice)[keyof typeof MerchantConsolidationBinFulfillableChoice];

export function fulfillableChoiceFromCriteria(
  fulfillable: boolean | undefined,
): MerchantConsolidationBinFulfillableChoice {
  if (fulfillable === undefined) {
    return MerchantConsolidationBinFulfillableChoice.any;
  }
  return fulfillable
    ? MerchantConsolidationBinFulfillableChoice.fulfillable
    : MerchantConsolidationBinFulfillableChoice.unfulfillable;
}

export function fulfillableChoiceToCriteria(
  choice: MerchantConsolidationBinFulfillableChoice,
): boolean | undefined {
  switch (choice) {
    case MerchantConsolidationBinFulfillableChoice.fulfillable:
      return true;
    case MerchantConsolidationBinFulfillableChoice.unfulfillable:
      return false;
    case MerchantConsolidationBinFulfillableChoice.any:
      return undefined;
  }
}

export const FULFILLABLE_CHOICE_LABELS: Record<
  MerchantConsolidationBinFulfillableChoice,
  string
> = {
  [MerchantConsolidationBinFulfillableChoice.any]: "Any",
  [MerchantConsolidationBinFulfillableChoice.fulfillable]: "Fulfillable only",
  [MerchantConsolidationBinFulfillableChoice.unfulfillable]:
    "Unfulfillable only",
};

export const CATCH_ALL_CRITERIA_SUMMARY = "Any inventory from this merchant";

const MAX_LISTED_VALUES = 3;

export function describeMerchantConsolidationBinCriteria(
  criteria: MerchantConsolidationBinCriteria,
): string {
  const parts: string[] = [];

  const skus = criteria.skus ?? [];
  if (skus.length > 0) {
    parts.push(describeValues("SKU", skus));
  }

  const grades = criteria.grades ?? [];
  if (grades.length > 0) {
    parts.push(describeValues("Grade", grades));
  }

  if (criteria.fulfillable !== undefined) {
    parts.push(
      FULFILLABLE_CHOICE_LABELS[
        fulfillableChoiceFromCriteria(criteria.fulfillable)
      ],
    );
  }

  return parts.length === 0 ? CATCH_ALL_CRITERIA_SUMMARY : parts.join(" · ");
}

function describeValues(noun: string, values: string[]): string {
  if (values.length > MAX_LISTED_VALUES) {
    return `${values.length} ${noun}s`;
  }
  return `${values.length === 1 ? noun : `${noun}s`}: ${values.join(", ")}`;
}

export interface ConsolidationBinLabelBatch {
  count: number;
  merchantName: string;
  /** The run's base name for a whole run, the bin's own name for one bin. */
  name: string;
}

export function describeConsolidationBinLabelBatch({
  count,
  merchantName,
  name,
}: ConsolidationBinLabelBatch): string {
  const owner = possessive(merchantName);
  return count === 1
    ? `1 label for ${owner} ${name} bin`
    : `${count} labels for ${owner} ${name} run`;
}

function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}
